import { Components, Theme } from '@mui/material/styles';
import { CSSProperties } from 'react';
import { WhiteLabelingColors } from '../../../state/ducks/company/types';
import { FBClasses } from '../../form.builder/defaults/layout';
import Colors from './utils/colors';

export default function themeOverrides (appTheme: Theme, customColors: WhiteLabelingColors): Components {
  const outlinedInputBorder = `1px solid ${Colors.mild_wind_blue}`;
  const popoverPaper: CSSProperties = {
    border: 'none !important',
    outline: outlinedInputBorder,
  };

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: Colors.white,
          overflow: 'hidden',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorDefault: {
          background: appTheme.palette.common.white,
        },
        colorPrimary: {
          background: 'transparent',
          color: appTheme.palette.text.primary,
        },
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        caption: { fontFamily: 'Sora, sans-serif' },
        body1: { fontFamily: 'Sora, sans-serif' },
        body2: {
          color: '#8A9DAD',
          fontSize: '0.8rem',
          fontFamily: 'Sora, sans-serif',
        },
        subtitle1: {
          color: appTheme.palette.text.primary,
          fontSize: '0.885rem',
          fontFamily: 'Sora, sans-serif',
        },
        subtitle2: {
          color: appTheme.palette.text.primary,
          fontSize: '0.8rem',
        },
        h5: {
          color: appTheme.palette.text.primary,
          fontSize: '1rem',
          fontFamily: 'Sora, sans-serif',
        },
        h4: {
          color: appTheme.palette.text.primary,
          fontSize: '1.4rem',
        },
        h6: {
          color: Colors.dark_gray,
          fontSize: '1rem',
          fontFamily: 'Sora, sans-serif',
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          borderRadius: 3,
          margin: appTheme.spacing(1),
          fontFamily: 'Sora, sans-serif',
          textTransform: 'capitalize',
          fontWeight: 600,
          '&.Mui-disabled': {
            backgroundColor: Colors.dawn,
            color: Colors.white,
          },
        },
        contained: {
          boxShadow: 'none',
        },
        text: {
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
            color: Colors.dawn,
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          '& .MuiFab-label': {
            lineHeight: '1rem',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontSize: '0.8rem',
          color: appTheme.palette.primary.main,
          fontFamily: 'Sora, sans-serif',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8rem',
          fontFamily: 'Sora, sans-serif',
          padding: 0,
        },
        head: {
          fontSize: '0.81rem',
          background: appTheme.palette.background.default,
          color: appTheme.palette.common.white,
          fontFamily: 'Sora, sans-serif',
          fontWeight: 'bold',
          lineHeight: '0.9rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.8rem',
          fontFamily: 'Sora, sans-serif',
          borderRadius: '4px',
          transition: appTheme.transitions.create('all'),
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.white,
          border: outlinedInputBorder,
          '&:not(.Mui-focused):not(.Mui-disabled):hover': {
            backgroundColor: Colors.background_white,
          },
          '&.Mui-focused': {
            borderColor: Colors.dodger_blue,
          },
          '&.Mui-disabled': {
            backgroundColor: Colors.whitest_grey,
          },
          '& fieldset': {
            border: 'none',
          },
          '& .MuiInputBase-inputMultiline': {
            padding: 0,
          },
        },
        input: {
          padding: 13,
          fontSize: 14,
        },
        notchedOutline: {
          borderRadius: 4,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          right: '10px',
        },
        select: {
          padding: appTheme.spacing(1.625, 3, 1.625, 1.5),
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': popoverPaper,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': popoverPaper,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&:hover': {
            background: 'none !important',
          },
          '&:not(.Mui-checked):not(.Mui-disabled):hover circle': {
            fill: Colors.background_white,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            '& .MuiSvgIcon-root': {
              color: appTheme.palette.action.disabled,
            },
          },
          '&.Mui-checked': {
            color: `${Colors.navy} !important`,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.8rem',
          maxWidth: '350px',
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          hyphens: 'auto',
          fontFamily: 'Sora, sans-serif',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8rem',
          fontFamily: 'Sora, sans-serif',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.9rem',
          color: appTheme.palette.text.primary,
        },
      },
    },
    MuiListItemSecondaryAction: {
      styleOverrides: {
        root: {
          right: appTheme.spacing(2),
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: appTheme.spacing(0, 1.9),
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: appTheme.spacing(1),
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: appTheme.spacing(1, 0),
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          fontFamily: 'Sora, sans-serif',
          [`& .${FBClasses.hoverVisibility}`]: {
            '&:hover': {
              '& .MuiButton-root': {
                visibility: 'visible',
              },
            },
            '& .MuiButton-root': {
              visibility: 'hidden',
            },
          },
          [`& .${FBClasses.inputPreview}`]: {
            '& fieldset': {
              borderWidth: '0px !important',
            },
            '& input': {
              padding: '13px !important',
            },
            "&[data-cy='password'] fieldset": {
              borderWidth: '1px !important',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'Sora, sans-serif',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          fontFamily: 'Sora, sans-serif',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: 'Sora, sans-serif',
          lineHeight: '15px',
          paddingLeft: 0,
          paddingTop: '5px',
          fontStyle: 'normal',
          fontWeight: 600,
          '&.Mui-selected': {
            color: Colors.navy,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: 'Sora, sans-serif',
          marginTop: appTheme.spacing(1),
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'Sora, sans-serif',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontFamily: 'Sora, sans-serif',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          fontFamily: 'Sora, sans-serif',
        },
      },
    },
  };
}
